import { useActivityStore } from '@/store/activity-store'
import type { TimelineLayout } from '@/store/activity-store'
import { cn } from '@/lib/utils'

const OPTIONS: { value: TimelineLayout; label: string; description: string }[] = [
  { value: 'combined', label: 'Combined', description: 'All metrics overlaid on one chart' },
  {
    value: 'stacked',
    label: 'Stacked',
    description: 'One panel per metric, sharing the time axis',
  },
]

export interface TimelineLayoutToggleProps {
  /** Called just before the layout changes (e.g. to remember the current zoom range). */
  onBeforeChange?: () => void
}

/**
 * "Combined | Stacked" segmented control for the timeline. A labelled
 * group of `aria-pressed` toggle buttons -- each is a native button, so
 * Tab reaches it and Enter/Space activate it. The choice is a persisted
 * UI preference (`ui.timelineLayout`).
 */
export function TimelineLayoutToggle({ onBeforeChange }: TimelineLayoutToggleProps) {
  const layout = useActivityStore((s) => s.ui.timelineLayout ?? 'combined')
  const setTimelineLayout = useActivityStore((s) => s.setTimelineLayout)

  return (
    <div
      role="group"
      aria-label="Timeline layout"
      className="border-input inline-flex h-8 overflow-hidden rounded-md border"
    >
      {OPTIONS.map((option) => {
        const pressed = layout === option.value
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={pressed}
            title={option.description}
            onClick={() => {
              if (pressed) return
              onBeforeChange?.()
              setTimelineLayout(option.value)
            }}
            className={cn(
              'focus-visible:ring-ring h-full px-3 text-xs font-medium outline-none focus-visible:ring-2 focus-visible:ring-inset',
              pressed
                ? 'bg-primary text-primary-foreground'
                : 'text-foreground hover:bg-accent hover:text-accent-foreground',
            )}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}
